import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  styled,
  Grid,
} from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import HistoryIcon from "@mui/icons-material/History";
import ApplyLoan from "../apply-loan/ApplyLoan";
import ResumeApplication from "../resume-application/ResumeApplication";
import ApplicationHistory from "../application-history/ApplicationHistory";

const PageWrapper = styled(Box)({
  backgroundColor: "#F5F6F7",
  minHeight: "100vh",
  padding: 0,
  margin: 0,
  display: "flex",
  justifyContent: "center",
});

const ContentContainer = styled(Box)({
  backgroundColor: "#FFFFFF",
  width: "100%",
  maxWidth: "1200px",
  padding: "24px 32px",
  boxShadow: "0px 0px 4px rgba(0, 0, 0, 0.05)",
  border: "1px solid #E5E8EB",
  margin: "24px 16px",
  borderRadius: "10px",
});

const PageTitle = styled(Typography)({
  color: "#0C53B7",
  fontFamily: "'Open Sans', sans-serif",
  fontWeight: 600,
  fontSize: "24px",
  lineHeight: "100%",
  letterSpacing: "0%",
  marginBottom: "8px",
});

const PageDescription = styled(Typography)({
  color: "#637381",
  fontFamily: "'Open Sans', sans-serif",
  fontSize: "14px",
  fontWeight: 400,
  fontStyle: "italic",
  lineHeight: "100%",
  letterSpacing: "0%",
  marginBottom: "24px",
});

const NavigationCard = styled(Card)(({ isActive }) => ({
  border: isActive ? "2px solid #0052CC" : "1px solid #E5E8EB",
  borderRadius: "10px",
  cursor: "pointer",
  transition: "all 0.3s ease",
  backgroundColor: isActive ? "#F8FAFF" : "#FFFFFF",
  "&:hover": {
    border: "2px solid #0052CC",
    backgroundColor: "#F8FAFF",
    transform: "translateY(-2px)",
    boxShadow: "0px 4px 12px rgba(0, 82, 204, 0.15)",
  },
}));

const NavigationCardContent = styled(CardContent)({
  padding: "24px",
  textAlign: "center",
  "&:last-child": {
    paddingBottom: "24px",
  },
});

const IconWrapper = styled(Box)(({ isActive }) => ({
  width: "64px",
  height: "64px",
  borderRadius: "50%",
  backgroundColor: isActive ? "#0052CC" : "#F5F6F7",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  margin: "0 auto 16px auto",
  color: isActive ? "#FFFFFF" : "#637381",
  transition: "all 0.3s ease",
}));

const CardTitle = styled(Typography)({
  color: "#212B36",
  fontFamily: "'Open Sans', sans-serif",
  fontWeight: 600,
  fontSize: "18px",
  lineHeight: "120%",
  marginBottom: "8px",
});

const CardDescription = styled(Typography)({
  color: "#637381",
  fontFamily: "'Open Sans', sans-serif",
  fontSize: "14px",
  fontWeight: 400,
  lineHeight: "140%",
});

const SubSectionCard = styled(Card)(({ isActive }) => ({
  border: isActive ? "2px solid #0052CC" : "1px solid #E5E8EB",
  borderRadius: "8px",
  cursor: "pointer",
  transition: "all 0.3s ease",
  backgroundColor: isActive ? "#F8FAFF" : "#FFFFFF",
  marginBottom: "12px",
  "&:hover": {
    border: "2px solid #0052CC",
    backgroundColor: "#F8FAFF",
  },
}));

const SubSectionCardContent = styled(CardContent)({
  padding: "16px 20px",
  "&:last-child": {
    paddingBottom: "16px",
  },
});

const SubSectionTitle = styled(Typography)({
  color: "#212B36",
  fontFamily: "'Open Sans', sans-serif",
  fontWeight: 500,
  fontSize: "16px",
  lineHeight: "120%",
});

const Application = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const getActiveSection = () => {
    const path = location.pathname;
    if (path.includes("/apply-loan")) return "new-application";
    if (path.includes("/resume-application")) return "resume-application";
    if (path.includes("/application-history")) return "submitted-application";
    return "overview";
  };

  const [activeSection, setActiveSection] = useState(getActiveSection());

  React.useEffect(() => {
    setActiveSection(getActiveSection());
  }, [location.pathname]);

  const handleSectionClick = (section) => {
    setActiveSection(section);
    switch (section) {
      case "new-application":
        navigate("/application/new-application");
        break;
      case "resume-application":
        navigate("/application/resume-application");
        break;
      case "submitted-application":
        navigate("/application/submitted-application");
        break;
      default:
        navigate("/application");
    }
  };

  const handleSubSectionClick = (subSection) => {
    setActiveSection("submitted-application");
    navigate("/application/submitted-application");
  };

  const navigationItems = [
    {
      id: "new-application",
      title: "New Application",
      description: "Start a fresh loan application",
      icon: <DescriptionIcon sx={{ fontSize: 32 }} />,
      path: "/application/new-application",
    },
    {
      id: "resume-application",
      title: "Saved/Resume Application",
      description: "Continue your incomplete applications",
      icon: <RestartAltIcon sx={{ fontSize: 32 }} />,
      path: "/application/resume-application",
    },
    {
      id: "submitted-application",
      title: "Submitted Application",
      description: "View and track your submitted applications",
      icon: <HistoryIcon sx={{ fontSize: 32 }} />,
      path: "/application/submitted-application",
      subSections: [
        {
          id: "application-history",
          title: "Application History",
          description: "View all your submitted applications",
        },
      ],
    },
  ];

  if (activeSection === "new-application") {
    return <ApplyLoan />;
  }
  
  if (activeSection === "resume-application") {
    return <ResumeApplication />;
  }
  
  if (activeSection === "submitted-application") {
    return <ApplicationHistory />;
  }

  return (
    <PageWrapper>
      <ContentContainer>

        <Grid container spacing={3} sx={{ backgroundColor: "red" }}>
          {navigationItems.map((item) => (
            <Grid item xs={12} md={4} key={item.id}>
              <NavigationCard
                isActive={activeSection === item.id}
                onClick={() => handleSectionClick(item.id)}
              >
                <NavigationCardContent>
                  <IconWrapper isActive={activeSection === item.id}>
                    {item.icon}
                  </IconWrapper>
                  <CardTitle>{item.title}</CardTitle>
                  <CardDescription>{item.description}</CardDescription>
                  
                  {item.subSections && (
                    <Box sx={{ mt: 2 }}>
                      {item.subSections.map((subSection) => (
                        <SubSectionCard
                          key={subSection.id}
                          isActive={activeSection === item.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSubSectionClick(subSection.id);
                          }}
                        >
                          <SubSectionCardContent>
                            <SubSectionTitle>{subSection.title}</SubSectionTitle>
                          </SubSectionCardContent>
                        </SubSectionCard>
                      ))}
                    </Box>
                  )}
                </NavigationCardContent>
              </NavigationCard>
            </Grid>
          ))}
        </Grid>
      </ContentContainer>
    </PageWrapper>
  );
};

export default Application;
